import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod/v3'
import { ArrowLeft, Save } from 'lucide-react'

import type { LotOption } from '@/components/lots/lot-items-field'
import type { CreateWithdrawalNoteRequest } from '@/lib/api/scenting-lot-api'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LotItemsField } from '@/components/lots/lot-items-field'
import { scentingLotApi } from '@/lib/api/scenting-lot-api'
import {
    ScanLotCodeButton,
    makeLotIdParser,
} from '@/components/lots/scan-lot-code-button'
import {
    useCreateScentingWithdrawalNote,
    useScentingLots,
} from '@/hooks/use-scenting-lot-query'
import { useDebounced } from '@/hooks/use-debounced'
import { scentingLotOption } from '@/components/lots/lot-option-builders'
import { m } from '@/lib/paraglide/messages'

const itemSchema = z.object({
    kind: z.literal('existing'),
    // LotItemsField dùng tên generic 'sorting_lot_id'; ở đây = scenting_lot_id.
    sorting_lot_id: z
        .string({ required_error: m.form_err_pick_existing_lot() })
        .uuid(m.form_err_pick_existing_lot()),
    quantity: z.coerce
        .number({
            required_error: m.form_err_quantity_required(),
            invalid_type_error: m.form_err_number_invalid(),
        })
        .positive(m.form_err_quantity_positive()),
})

const schema = z.object({
    notes: z
        .string()
        .min(1, m.withdrawal_title_required())
        .max(2000, m.form_err_notes_too_long()),
    items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
})

type FormData = z.infer<typeof schema>

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/scenting-warehouse/withdrawal-notes/new',
)({
    component: NewWithdrawalPage,
})

function useLotOptionsAndCache() {
    const [lotSearch, setLotSearch] = useState('')
    const debouncedSearch = useDebounced(lotSearch)
    const { data: lotsData } = useScentingLots({
        per_page: 50,
        only_available: '1',
        search: debouncedSearch || undefined,
    })
    const lotOptions = useMemo<LotOption[]>(
        () => (lotsData?.data ?? []).map(scentingLotOption),
        [lotsData],
    )
    const [scannedLots, setScannedLots] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await scentingLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, scentingLotOption(l))
                return next
            })
        } catch {
            // ignore
        }
    }
    return { setLotSearch, lotOptions, scannedLots, fetchAndCacheLot }
}

function NewWithdrawalPage() {
    const navigate = useNavigate()
    const create = useCreateScentingWithdrawalNote()
    const { setLotSearch, lotOptions, scannedLots, fetchAndCacheLot } =
        useLotOptionsAndCache()

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            notes: '',
            items: [{ kind: 'existing', sorting_lot_id: '', quantity: 0 }],
        },
    })

    const handleSubmit = async (value: CreateWithdrawalNoteRequest) => {
        try {
            const res = await create.mutateAsync(value)
            const noteId = res.data?.id
            if (noteId) {
                void navigate({
                    to: '/scenting-warehouse/withdrawal-notes/$id',
                    params: { id: noteId },
                })
            }
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    const submit = form.handleSubmit(
        (data) =>
            void handleSubmit({
                notes: data.notes || undefined,
                items: data.items.map((it) => ({
                    scenting_lot_id: it.sorting_lot_id,
                    quantity: Number(it.quantity),
                })),
            }),
    )

    const onCancel = () =>
        navigate({
            to: '/scenting-warehouse/withdrawal-notes',
            search: { page: 1 },
        })

    return (
        <div className="space-y-4 p-4 pb-24 md:p-6 md:pb-6">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={onCancel}>
                    <ArrowLeft className="size-4 mr-1" /> {m.sl_lot_back()}
                </Button>
                <h1 className="text-xl font-semibold">
                    {m.scl_new_plh_title()}
                </h1>
            </div>

            <p className="max-w-4xl text-sm text-muted-foreground">
                {m.scl_plh_intro()}
            </p>

            <FormProvider {...form}>
                <form
                    onSubmit={submit}
                    className="space-y-3 md:space-y-6 max-w-4xl"
                >
                    <div className="rounded-md border bg-card p-4">
                        <Label className="mb-1 block">
                            {m.withdrawal_title_label()}{' '}
                            <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            {...form.register('notes')}
                            placeholder={m.withdrawal_title_placeholder()}
                        />
                        {form.formState.errors.notes && (
                            <p className="mt-1 text-sm text-destructive">
                                {form.formState.errors.notes.message}
                            </p>
                        )}
                    </div>

                    <LotItemsField
                        mode="out"
                        lotOptions={lotOptions}
                        scannedLots={Array.from(scannedLots.values())}
                        onLotSearchChange={setLotSearch}
                        categoryOptions={[]}
                        categoryLabel={m.sl_lot_grade()}
                        headerAction={
                            <ScanLotCodeButton
                                parseLotId={makeLotIdParser(
                                    'scenting-warehouse/lots',
                                )}
                                onScanned={(lotId) => {
                                    const current =
                                        form.getValues('items') ?? []
                                    form.setValue(
                                        'items',
                                        [
                                            ...current,
                                            {
                                                kind: 'existing',
                                                sorting_lot_id: lotId,
                                                quantity: 0,
                                            },
                                        ],
                                        {
                                            shouldDirty: true,
                                            shouldValidate: false,
                                        },
                                    )
                                    void fetchAndCacheLot(lotId)
                                }}
                            />
                        }
                    />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={create.isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {create.isPending
                                ? m.sl_new_submitting()
                                : m.sl_new_submit_pnk()}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}
